/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type PropsWithChildren, type ReactNode } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useDarkModeColors } from '@atlassian/color-picker';

import { useProductDependencies } from '../../controllers/use-product-dependencies';
import { CommonLayout, Height } from '../../types';

const { NO_IMAGE_BACKGROUND, SPLIT_IMAGE_LEFT, SPLIT_IMAGE_RIGHT, FRAME_ABOVE_IMAGE } =
	CommonLayout;

/**
 * The maximum width in pixels at which the layout is oriented vertically, at larger widths it is horizontal.
 */
export const VERTICAL_MAX_WIDTH = 640;

const FRAME_MAX_WIDTH = 486;
const CENTERED_FRAME_WIDTH = 364;
const WIDE_FRAME_WIDTH = 393;
const FULL_WIDTH_FRAME_WIDTH = 512;

const percentageStyles = cssMap({
	flex: {
		display: 'flex',
		height: '100%',
		overflow: 'hidden',
	},
	imageSide: {
		flexGrow: 0,
		flexShrink: 0,
	},
	childrenSide: {
		flexGrow: 1,
	},
	borderStyle: {
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
});

const gridStyles = cssMap({
	grid: {
		display: 'grid',
		height: '100%',
		gridTemplateRows: '1fr auto 1fr',
	},
	frameChildren: {
		gridColumn: '1 / 2',
		gridRow: '2 / 3',
		borderRadius: token('radius.large'),
		marginInline: token('space.400'),
	},
	fullImage: {
		gridColumn: '1 / -1',
		gridRow: '1 / -1',
		height: '100%',
		overflow: 'hidden',
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	roundedBorderRadius: {
		borderRadius: token('radius.xlarge'),
	},
	smallFrameStyling: {
		maxWidth: FRAME_MAX_WIDTH,
		marginInline: token('space.200'),
	},
	centeredFrameStyling: {
		width: CENTERED_FRAME_WIDTH,
		maxWidth: FRAME_MAX_WIDTH,
		marginInline: token('space.400'),
	},
	wideFrameStyling: {
		width: WIDE_FRAME_WIDTH,
		maxWidth: FRAME_MAX_WIDTH,
		marginInline: token('space.400'),
	},
	fullWidthFrameStyling: {
		width: FULL_WIDTH_FRAME_WIDTH,
		marginInline: token('space.800'),
	},
});

function spotlightToCssHeight(height: Height): number {
	switch (height) {
		case Height.SMALL:
			return 248;
		case Height.MEDIUM:
			return 400;
		case Height.LARGE:
		default:
			return 620;
	}
}

export enum SpotlightWidth {
	SMALL = 356,
	CENTERED = 800,
	WIDE = 1050,
	FULL_WIDTH = 1840,
}

export function getContentFrameStyling(width: number): string {
	if (width <= SpotlightWidth.SMALL) {
		return 'smallFrameStyling';
	}
	if (width <= SpotlightWidth.CENTERED) {
		return 'centeredFrameStyling';
	}
	if (width <= SpotlightWidth.WIDE) {
		return 'wideFrameStyling';
	}
	return 'fullWidthFrameStyling';
}

export type LayoutProps = PropsWithChildren<{
	backgroundColor: string;
	hasRoundedCorners?: boolean;
	height: Height;
	image?: ReactNode;
	imageWidthPercentage?: number;
	layout: CommonLayout;
	testId?: string;
	width?: number;
}>;

export function SpotlightLayout({
	backgroundColor: savedBackgroundColor,
	children,
	height,
	image,
	layout,
	testId,
	width = VERTICAL_MAX_WIDTH + 1,
	imageWidthPercentage = 0.5,
	hasRoundedCorners,
}: LayoutProps): JSX.Element {
	const { isSSRProductInDarkMode, hideExtensionBorders } = useProductDependencies();
	const { getDarkModeCompatibleBackgroundColor } = useDarkModeColors(isSSRProductInDarkMode);
	const backgroundColor = getDarkModeCompatibleBackgroundColor(savedBackgroundColor);
	if (!image) {
		layout = NO_IMAGE_BACKGROUND;
	}
	const showVerticalLayout = width < VERTICAL_MAX_WIDTH;

	// In the small-height layout, if there's an image, its width is fixed:
	let imageWidth: number | undefined;
	if (height === Height.SMALL && (layout === SPLIT_IMAGE_LEFT || layout === SPLIT_IMAGE_RIGHT)) {
		if (width >= 1024) {
			imageWidth = 514;
		} else if (width >= 768) {
			imageWidth = 186;
		} else if (width > VERTICAL_MAX_WIDTH) {
			imageWidth = 140;
		} else {
			imageWidth = 84;
		}
	}

	const getFlexDirection = () => {
		if (showVerticalLayout) {
			return layout === SPLIT_IMAGE_LEFT ? 'column' : 'column-reverse';
		}
		return layout === SPLIT_IMAGE_LEFT ? 'row' : 'row-reverse';
	};

	return layout === FRAME_ABOVE_IMAGE ? (
		<div
			css={gridStyles.grid}
			data-testid={testId}
			style={{
				height: `${spotlightToCssHeight(height)}px`,
			}}
		>
			<div css={[gridStyles.fullImage, hasRoundedCorners && gridStyles.roundedBorderRadius]}>
				{image}
			</div>
			<div
				css={[
					gridStyles.frameChildren,
					gridStyles[getContentFrameStyling(width) as keyof typeof gridStyles],
				]}
				style={{
					backgroundColor,
				}}
			>
				{children}
			</div>
		</div>
	) : (
		<div
			css={[percentageStyles.flex, !hideExtensionBorders && percentageStyles.borderStyle]}
			data-testid={testId}
			style={{
				backgroundColor,
				height: `${spotlightToCssHeight(height)}px`,
				flexDirection: getFlexDirection(),
				borderRadius: hasRoundedCorners ? token('radius.xlarge') : 0,
			}}
		>
			{layout !== NO_IMAGE_BACKGROUND && (
				<div
					css={[percentageStyles.imageSide]}
					style={{
						flexBasis: imageWidth || `${imageWidthPercentage * 100}%`,
						height: showVerticalLayout
							? `${spotlightToCssHeight(height) * imageWidthPercentage}px`
							: '100%',
					}}
				>
					{image}
				</div>
			)}

			<div css={[percentageStyles.childrenSide]}>{children}</div>
		</div>
	);
}
